import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, PencilSimple, Trash, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function CrudPage({
  title, subtitle, endpoint, columns, fields, testId, defaultForm = {}, rowActions,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(defaultForm);

  const load = () => {
    setLoading(true);
    api.get(`/${endpoint}`).then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(defaultForm); setEdit(null); setOpen(true); };
  const openEdit = (it) => { setForm(it); setEdit(it); setOpen(true); };

  const save = async () => {
    try {
      if (edit) {
        await api.put(`/${endpoint}/${edit.id}`, form);
        toast.success('تم التحديث');
      } else {
        await api.post(`/${endpoint}`, form);
        toast.success('تم الإضافة');
      }
      setOpen(false); load();
    } catch (err) {
      toast.error('فشل الحفظ: ' + (err.response?.data?.detail || err.message));
    }
  };

  const del = async (it) => {
    if (!window.confirm('هل تريد الحذف؟')) return;
    try {
      await api.delete(`/${endpoint}/${it.id}`);
      toast.success('تم الحذف');
      load();
    } catch { toast.error('فشل الحذف'); }
  };

  const filtered = items.filter(i => {
    if (!q) return true;
    return Object.values(i).some(v => String(v || '').toLowerCase().includes(q.toLowerCase()));
  });

  return (
    <div className="space-y-5" data-testid={testId}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">{subtitle}</div>
          <h1 className="text-3xl font-bold text-slate-900">{title}</h1>
        </div>
        <Button onClick={openNew} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="crud-add-button">
          <Plus className="ms-1" size={16} /> إضافة جديد
        </Button>
      </div>

      <Card className="p-4 border border-slate-200 rounded-md shadow-none">
        <div className="relative max-w-md">
          <MagnifyingGlass className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث..." className="pr-9" />
        </div>
      </Card>

      <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm data-table">
            <thead className="bg-slate-50">
              <tr>
                {columns.map(c => <th key={c.key} className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">{c.label}</th>)}
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 1} className="p-6 text-center text-slate-500">جاري التحميل...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="p-8 text-center text-slate-500">لا توجد بيانات</td></tr>
              ) : filtered.map((it) => (
                <tr key={it.id} className="border-t border-slate-100">
                  {columns.map(c => <td key={c.key} className="p-3">{c.render ? c.render(it) : (it[c.key] || '—')}</td>)}
                  <td className="p-3 text-end">
                    <div className="flex justify-end gap-1 items-center">
                      {rowActions && rowActions(it)}
                      <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-${it.id}`}><PencilSimple size={16} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => del(it)} data-testid={`delete-${it.id}`}><Trash size={16} className="text-red-600" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? 'تعديل' : 'إضافة'} — {title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {fields.map(f => (
              <div key={f.key} className={f.full ? 'col-span-2' : ''}>
                <Label className="mb-1 block">{f.label}</Label>
                {f.type === 'textarea' ? (
                  <Textarea value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={3} />
                ) : f.type === 'select' ? (
                  <select
                    value={form[f.key] || ''}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                  >
                    <option value="">—</option>
                    {f.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <Input
                    type={f.type || 'text'}
                    value={form[f.key] || ''}
                    onChange={(e) => setForm({ ...form, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value })}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} className="bg-slate-900 text-white" data-testid="crud-save-button">حفظ</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
